/**
 * Construction Debris Weight and Dumpster Calculator — public calculator page
 * (PRP-CALC-1 S3). Converts tear-out quantities into cubic yards and weight,
 * then recommends a dumpster size governed by the weight limit, not just volume.
 * Carries the /tools SEO treatment: per-route meta + JSON-LD, card-wrapped form,
 * roof tear-off content section, app promo band.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  NumberField,
  SelectField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import { CalcAPI, type CalcResponse, type DumpsterSize } from './calcApi';

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'What size dumpster do I need?',
    a: 'On restoration and roofing jobs the weight limit usually decides it before the volume does. Shingles and plaster hit the tonnage cap in a half-full can, so a 20 yard box that holds a two-ton limit is full at weight with room to spare. As a rough guide: a 12 yard suits a single bath or a small tear-out, a 20 yard covers most whole-house interior gut or a 15 to 25 square reroof, a 30 yard handles a larger reroof or a fire gut, and a 40 yard is for bulky light debris like insulation and framing where you run out of space before you run out of weight. Enter your quantities above and the tool sizes by whichever limit you hit first.',
  },
  {
    q: 'How much does a square of shingles weigh?',
    a: 'A square of asphalt shingles runs about 230 to 250 pounds per layer, so one square of three-tab is roughly a quarter ton and architectural shingles sit a little heavier. Tear-off weight climbs with every layer that comes off. A two-layer roof is close to half a ton per square, and a three-layer strip is near three-quarters of a ton before you count felt, nails, and the odd course of wood.',
  },
  {
    q: 'Why do the layers matter so much?',
    a: 'Each layer you strip adds its own square weight to the pile. Twenty squares at one layer is about two and a half tons; the same roof at three layers is over seven tons. That is the difference between one haul and three, so a re-cover history on the roof changes the dumpster count more than the roof size does.',
  },
  {
    q: 'Do wet materials weigh more?',
    a: 'Yes, and it is not a small effect. Waterlogged drywall, carpet pad, and insulation weigh two to three times their dry weight. Half-inch drywall is about 1.8 pounds per square foot dry and soaks up well past that after a category loss. If you are hauling out saturated tear-out, size up or plan an extra haul rather than trust the dry table.',
  },
  {
    q: 'What is an overage fee?',
    a: 'Roll-off pricing includes a tonnage allowance, and anything over it is billed per ton at the scale, commonly $50 to $100 a ton depending on the market and the material. Heavy debris is exactly where this bites, since the box looks half empty while the ticket says you are over. Sizing by weight up front is how you avoid the surprise line on the disposal invoice.',
  },
  {
    q: 'Can I mix debris, or does it have to be a clean load?',
    a: 'Mixed construction and demolition debris runs about 417 to 500 pounds per cubic yard and goes to a C&D transfer station at the standard rate. A clean single-material load, all shingles or all concrete, often qualifies for a lower recycling rate but only if it stays uncontaminated. If you can keep the roof tear-off in its own can it can pay off, but one contaminated load loses the clean rate for the whole box.',
  },
  {
    q: 'How many squares of shingles fit in a 20 yard dumpster?',
    a: 'Weight caps it, not volume. A 20 yard box with a two-ton limit takes roughly 16 squares of single-layer shingles before it is at weight, and about 8 squares if the roof is a two-layer tear-off. The pile will not look full at that point, which trips up crews who load by eye. Load by the square count, not by how much room is left.',
  },
  {
    q: 'How do I bill debris removal in Xactimate?',
    a: 'Debris hauling goes on as DMO dumpster-load items priced against your price list, one line per haul at the container size you used. The tool gives you the volume, weight, and haul count the estimate is built on, and in the app the DMO line lands in the estimate for you. Keep the disposal ticket; the tonnage on it is what defends the haul count if the desk adjuster pushes back.',
  },
];

export default function DumpsterCalculatorPage() {
  useSeo({
    title:
      'Debris & Dumpster Weight Calculator — Shingles, Drywall, C&D | Antero Trail',
    description:
      'Free debris weight calculator. Turn roof, drywall, and tear-out quantities into cubic yards, tons, and the right dumpster size for restoration and roofing jobs.',
    path: '/tools/dumpster-weight-calculator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Construction Debris Weight and Dumpster Calculator',
        description:
          'Converts tear-out quantities into cubic yards and tons, then recommends a dumpster size governed by the weight limit rather than volume alone.',
        path: '/tools/dumpster-weight-calculator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [roofingSquares, setRoofingSquares] = useState<number>(0);
  const [roofingLayers, setRoofingLayers] = useState<number>(1);
  const [drywallSf, setDrywallSf] = useState<number>(0);
  const [carpetSy, setCarpetSy] = useState<number>(0);
  const [framingLf, setFramingLf] = useState<number>(0);
  const [otherCy, setOtherCy] = useState<number>(0);
  const [directCy, setDirectCy] = useState<number | ''>('');
  const [dumpsterSize, setDumpsterSize] = useState<DumpsterSize | 0>(0);

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const anyQty =
      roofingSquares > 0 ||
      drywallSf > 0 ||
      carpetSy > 0 ||
      framingLf > 0 ||
      otherCy > 0 ||
      (directCy !== '' && Number(directCy) > 0);
    if (!anyQty) {
      setError('Enter at least one debris quantity.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await CalcAPI.debris(
        {
          roofing_squares: roofingSquares,
          roofing_layers: roofingLayers,
          drywall_sf: drywallSf,
          carpet_sy: carpetSy,
          framing_lf: framingLf,
          other_cy: otherCy,
          ...(directCy !== '' ? { direct_cy: Number(directCy) } : {}),
          ...(dumpsterSize ? { dumpster_size_yd: dumpsterSize } : {}),
        },
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Debris — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Debris weight and dumpster size calculator
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Turn tear-out quantities into cubic yards and tons, then find the
        dumpster size that fits the job. Roofing, drywall, and other heavy debris
        hit the weight limit long before they fill the box, so the tool sizes by
        weight, not just volume. Enter what you are removing and it returns the
        volume, the tonnage, and how many hauls you need.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Debris inputs"
        >
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Roofing"
              suffix="squares"
              value={roofingSquares}
              min={0}
              onChange={setRoofingSquares}
              hint="One square = 100 SF of roof."
            />
            <NumberField
              label="Roofing layers"
              value={roofingLayers}
              min={1}
              step="1"
              onChange={setRoofingLayers}
              hint="Number of shingle layers being torn off."
            />
          </div>
          <NumberField
            label="Drywall"
            suffix="SF"
            value={drywallSf}
            min={0}
            onChange={setDrywallSf}
          />
          <NumberField
            label="Carpet and pad"
            suffix="SY"
            value={carpetSy}
            min={0}
            onChange={setCarpetSy}
            hint="Square yards of carpet being removed."
          />
          <NumberField
            label="Framing lumber"
            suffix="LF"
            value={framingLf}
            min={0}
            onChange={setFramingLf}
          />
          <NumberField
            label="Other debris"
            suffix="CY"
            value={otherCy}
            min={0}
            onChange={setOtherCy}
            hint="Loose volume of anything not listed above, in cubic yards."
          />
          <NumberField
            label="Known total volume"
            suffix="CY"
            value={directCy}
            min={0}
            onChange={(v) => setDirectCy(Number.isNaN(v) ? '' : v)}
            hint="Optional. Use this if you already know the total cubic yards."
          />
          <SelectField<string>
            label="Dumpster size (optional)"
            value={String(dumpsterSize)}
            onChange={(v) => setDumpsterSize(Number(v) as DumpsterSize | 0)}
            options={[
              { value: '0', label: 'Let the tool recommend' },
              { value: '12', label: '12 yard' },
              { value: '20', label: '20 yard' },
              { value: '30', label: '30 yard' },
              { value: '40', label: '40 yard' },
            ]}
          />

          <Button type="submit" isLoading={loading} className="w-full">
            Size the dumpster
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter your tear-out quantities and select Size the dumpster to see
              volume, weight, and the number of hauls.
            </div>
          )}
        </div>
      </div>

      <section className="mt-14">
        <DimensionDivider label="Roof tear-off math" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          How much do shingles weigh, and why it decides the dumpster
        </h2>
        <div className="max-w-3xl space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Roofing debris weight per square is the number that governs a
            reroof haul. A square of asphalt shingles is about 230 to 250 pounds
            for a single layer, so 20 squares of single-layer tear-off is roughly
            2.4 tons. That already fills a 20 yard box to its weight limit while
            the container still looks half empty. The mistake crews make is
            loading by eye until the box is full, then getting a scale ticket
            that runs over the tonnage allowance.
          </p>
          <p>
            Layers are the multiplier. Every layer you strip adds its own square
            weight, so the same 20 square roof is about 2.4 tons at one layer,
            4.8 tons at two, and over 7 tons at three. A two-layer or three-layer
            re-cover turns one haul into two or three, which is why the roof
            history matters more to the dumpster count than the roof area does.
            Check the eaves before you order the can.
          </p>
          <p>
            Interior tear-out stacks on top of the same weight logic. Half-inch
            drywall is about 1.8 pounds per square foot, and mixed construction
            and demolition debris averages 417 to 500 pounds per cubic yard. Wet
            material from a category loss weighs two to three times its dry
            figure. The calculator runs each material through its own weight
            factor, adds them up, and reports which limit, weight or volume,
            drives your haul count.
          </p>
        </div>
      </section>

      <AppPromo
        className="mt-14"
        context="In the app, tear-out quantities flow from the scope itself, and debris lines land in the estimate priced as DMO dumpster-load items, with no re-keying of the haul count."
      />

      <MethodSection title="How volume and weight are figured">
        <p>
          Each material is converted to loose cubic yards using published debris
          conversion factors, then to weight using a material weight table. This
          two-step approach matters because volume and weight do not track
          together. A roof tear-off is a good example: a few squares of
          shingles take up little space in the box but weigh a great deal, and
          tear-off weight climbs with each layer that comes off. Drywall,
          plaster, and concrete are similarly heavy for their volume, while
          carpet and light framing are bulky but light.
        </p>
        <p>
          The dumpster recommendation is governed by whichever limit you hit
          first. Roll-off dumpsters have both a volume in cubic yards and a
          weight limit in tons, and heavy debris almost always reaches the
          weight limit before the box is full. The tool compares your total
          weight against the container tonnage limit and the volume against the
          box size, then reports how many hauls you need and which limit is
          driving the count. When a single heavy load would exceed a container's
          tonnage, it flags that so you are not surprised by an overage charge at
          the scale.
        </p>
      </MethodSection>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'EPA construction and demolition debris conversion factors',
            detail:
              'Volume-to-weight conversion factors used to turn material quantities into tons.',
          },
          {
            name: 'Roofing and building material weight references',
            detail:
              'Per-square and per-unit weights for shingles, drywall, and framing used in the weight table.',
          },
          {
            name: 'Xactimate DMO dumpster-load items (priced at import)',
            detail:
              'Standard 12, 20, 30, and 40 yard container sizes with their published weight limits. The recommended load resolves to a real Xactimate DMO item; Xactimate prices it at import.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
